import React, { useState, useEffect } from 'react';
import {
  Card, Button, Form, Select, InputNumber, Input, Space, Typography, message,
  Spin, Tag
} from 'antd';
import {
  SendOutlined, ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import logger from '../../services/logger';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * ControlPanel Component
 *
 * Control komutlarını API'den dinamik olarak alır ve form oluşturur.
 * Gönderim sonrası response/timeout durumunu gösterir.
 */
const ControlPanel = ({
  selectedDevice,
  onCommandSent,
  mode = 'single',
  selectedDevices = [],
  onBulkCommandStart,
  lastDeviceResponse
}) => {
  const [commands, setCommands] = useState([]);
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [commandSchema, setCommandSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commandLoading, setCommandLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchControlCommands();
  }, []);

  useEffect(() => {
    if (selectedCommand) {
      fetchCommandSchema(selectedCommand);
    } else {
      setCommandSchema(null);
    }
  }, [selectedCommand]);

  const fetchControlCommands = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/v1/mqtt/control-commands');
      setCommands(response.data.commands || []);
    } catch (error) {
      logger.error('Control commands fetch error:', error);
      message.error('Control komutları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommandSchema = async (commandName) => {
    try {
      const response = await axios.get(`/api/v1/mqtt/control-commands/${commandName}/schema`);
      setCommandSchema(response.data);
    } catch (error) {
      logger.error('Command schema fetch error:', error);
      setCommandSchema(null);
    }
  };

  const handleSendCommand = async (values) => {
    if (mode === 'single') {
      if (!selectedDevice) {
        message.error('Lütfen bir cihaz seçin');
        return;
      }
      await sendSingleCommand(values);
    } else {
      if (!selectedDevices || selectedDevices.length === 0) {
        message.error('Lütfen en az bir cihaz seçin');
        return;
      }
      await sendBulkCommand(values);
    }
  };

  const sendSingleCommand = async (values) => {
    try {
      setCommandLoading(true);
      setLastResponse(null);

      const params = { ...values };
      delete params.command;
      delete params.timeout_seconds;

      const response = await axios.post(`/api/v1/mqtt/devices/${selectedDevice.id}/control`, {
        command: selectedCommand,
        params: params,
        timeout_seconds: values.timeout_seconds || commandSchema?.default_timeout || 30
      });

      setLastResponse({
        success: true,
        queued: response.data.queued,
        command: selectedCommand,
        device: selectedDevice.name,
        command_id: response.data.command_id,
        timeout_seconds: response.data.timeout_seconds,
        sent_at: response.data.sent_at,
        response: response.data
      });

      // Cihaz offline ise komut kuyruğa alınır; kullanıcıyı bilgilendir
      if (response.data.queued) {
        message.info(response.data.message || 'Cihaz çevrimdışı: komut kuyruğa alındı, online olunca gönderilecek');
      }

      if (onCommandSent) {
        onCommandSent(response.data);
      }
    } catch (error) {
      logger.error('Control command error:', error);
      setLastResponse({
        success: false,
        command: selectedCommand,
        device: selectedDevice.name,
        error: error.response?.data?.detail || error.message
      });
      message.error('Komut gönderilemedi');
    } finally {
      setCommandLoading(false);
    }
  };

  const sendBulkCommand = async (values) => {
    try {
      setCommandLoading(true);

      const params = { ...values };
      delete params.command;
      delete params.timeout_seconds;

      const response = await axios.post('/api/v1/mqtt/batch/control', {
        device_ids: selectedDevices,
        command: selectedCommand,
        params: params,
        timeout_seconds: values.timeout_seconds || commandSchema?.default_timeout || 30
      });

      message.success(`Batch işlem başlatıldı: ${response.data.batch_id}`);

      if (onBulkCommandStart) {
        onBulkCommandStart(response.data);
      }
    } catch (error) {
      logger.error('Bulk control command error:', error);
      message.error(error.response?.data?.detail || 'Batch işlem başlatılamadı');
    } finally {
      setCommandLoading(false);
    }
  };

  // Render dynamic form fields based on schema
  const renderFormFields = () => {
    if (!commandSchema || !commandSchema.properties) {
      return null;
    }

    const properties = commandSchema.properties;

    return Object.entries(properties).map(([key, prop]) => {
      // Skip internal fields
      if (key.startsWith('_')) return null;

      const isRequired = commandSchema.required?.includes(key);
      const rules = isRequired ? [{ required: true, message: `${prop.title || key} gerekli` }] : [];

      // Number field
      if (prop.type === 'integer' || prop.type === 'number') {
        return (
          <Form.Item
            key={key}
            name={key}
            label={prop.title || key}
            rules={rules}
            tooltip={prop.description}
            initialValue={prop.default}
          >
            <InputNumber
              min={prop.minimum}
              max={prop.maximum}
              placeholder={prop.description}
              style={{ width: '100%' }}
            />
          </Form.Item>
        );
      }

      // String field
      if (prop.type === 'string') {
        if (prop.enum) {
          return (
            <Form.Item
              key={key}
              name={key}
              label={prop.title || key}
              rules={rules}
              tooltip={prop.description}
              initialValue={prop.default}
            >
              <Select placeholder={prop.description || `${prop.title || key} seçin`} style={{ width: '100%' }}>
                {prop.enum.map(val => (
                  <Option key={val} value={val}>{val}</Option>
                ))}
              </Select>
            </Form.Item>
          );
        }

        return (
          <Form.Item
            key={key}
            name={key}
            label={prop.title || key}
            rules={rules}
            tooltip={prop.description}
            initialValue={prop.default}
          >
            <Input placeholder={prop.description} style={{ width: '100%' }} />
          </Form.Item>
        );
      }

      return null;
    });
  };

  // Response render fonksiyonları sağ panele taşındı (MQTTControl.js)

  return (
    <Card
      title={
        <Space>
          <SendOutlined />
          <span>Control Komutu Gönder</span>
          {mode === 'bulk' && <Tag color="blue">{selectedDevices.length} cihaz seçili</Tag>}
        </Space>
      }
      extra={<Button icon={<ReloadOutlined />} onClick={fetchControlCommands} loading={loading} size="small" />}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin tip="Komutlar yükleniyor..." />
        </div>
      ) : (
        <>
          {/* Compact Form */}
          <Form form={form} layout="inline" onFinish={handleSendCommand} style={{ flexWrap: 'wrap', gap: 8 }}>
            <Form.Item style={{ minWidth: 200, flex: 1 }} required>
              <Select
                placeholder="Komut seçin..."
                value={selectedCommand}
                onChange={(value) => { setSelectedCommand(value); form.resetFields(); }}
              >
                {commands.map(cmd => (
                  <Option key={cmd.name} value={cmd.name}>
                    {cmd.name} <Text type="secondary" style={{ fontSize: 10 }}>- {cmd.description}</Text>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* Dynamic Fields inline */}
            {renderFormFields()}

            <Form.Item name="timeout_seconds" initialValue={commandSchema?.default_timeout || 30}>
              <InputNumber min={5} max={300} style={{ width: 90 }} addonAfter="s" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={commandLoading}
                disabled={!selectedCommand || (mode === 'single' && !selectedDevice) || (mode === 'bulk' && selectedDevices.length === 0)}
                icon={<SendOutlined />}
              >
                {mode === 'single' ? 'Gönder' : `${selectedDevices.length} Cihaza`}
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
    </Card>
  );
};

export default ControlPanel;
